import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Instagram, Twitter, Linkedin, Mail, Phone } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export default function Footer() {
  const { isDark } = useTheme();
  const logoFilter = isDark ? 'brightness(0) invert(1)' : 'brightness(0)';

  return (
    <footer className={`global-footer border-t ${isDark ? 'bg-slate-950 text-muted-foreground border-border' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/eventism-logo.png" alt="Eventism" className="h-5 sm:h-6 md:h-7" style={{ filter: logoFilter }} />
            </Link>
            <p className={`text-sm ${isDark ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
              Exceptional events. Instant quotes. Professional service for Denver and beyond.
            </p>
            <div className="space-y-2 text-sm">
              <a href="mailto:team@eventism.com" className={`flex items-center gap-2 transition-colors ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-slate-900'}`}>
                <Mail className="h-4 w-4" /> team@eventism.com
              </a>
              <a href="tel:7209074044" className={`flex items-center gap-2 transition-colors ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-slate-900'}`}>
                <Phone className="h-4 w-4" /> (720) 907-4044
              </a>
            </div>
            <div className="flex space-x-4 pt-2">
              <a href="#" className={`transition-colors ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-slate-900'}`}>
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className={`transition-colors ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-slate-900'}`}>
                <Twitter className="h-5 w-5" />
              </a>
              <a href="#" className={`transition-colors ${isDark ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground hover:text-slate-900'}`}>
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className={`font-heading font-semibold mb-4 ${isDark ? 'text-foreground' : 'text-slate-900'}`}>Services</h3>
            <ul className="space-y-2">
              <li><Link to="/plan-event" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Hire Vendors</Link></li>
              <li><Link to="/hire-us" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Request Custom Quote</Link></li>
              <li><Link to="/shifts" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Job Board</Link></li>
              <li><Link to="/b2b/pricing" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>B2B Membership</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className={`font-heading font-semibold mb-4 ${isDark ? 'text-foreground' : 'text-slate-900'}`}>Company</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>About Us</Link></li>
              <li><Link to="/how-it-works" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>How It Works</Link></li>
              <li><Link to="/contact" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Contact Us</Link></li>
              <li><Link to="/register" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Become a Vendor</Link></li>
              <li><Link to="/promo-partners" className={`text-sm transition-colors font-medium ${isDark ? 'hover:text-foreground text-emerald-400' : 'hover:text-slate-900 text-emerald-600'}`} data-testid="footer-promo-partners">💰 Earn as a Promo Partner</Link></li>
            </ul>
          </div>

          {/* For Organizers */}
          <div data-testid="footer-for-organizers">
            <h3 className={`font-heading font-semibold mb-4 ${isDark ? 'text-foreground' : 'text-slate-900'}`}>For Organizers</h3>
            <ul className="space-y-2">
              <li><Link to="/sell-tickets" className={`text-sm font-medium transition-colors ${isDark ? 'hover:text-foreground text-amber-400' : 'hover:text-slate-900 text-amber-600'}`} data-testid="footer-sell-tickets">Sell tickets on Eventism</Link></li>
              <li><Link to="/organizations/apply" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`} data-testid="footer-org-apply">Create an organization</Link></li>
              <li><Link to="/organizations" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`} data-testid="footer-org-directory">Browse organizers</Link></li>
              <li><Link to="/events" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>See live events</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className={`font-heading font-semibold mb-4 ${isDark ? 'text-foreground' : 'text-slate-900'}`}>Support</h3>
            <ul className="space-y-2">
              <li><Link to="/how-it-works" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>FAQs</Link></li>
              <li><Link to="/contact" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Help Center</Link></li>
              <li><Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Privacy Policy</Link></li>
              <li><Link to="/terms" className={`text-sm transition-colors ${isDark ? 'hover:text-foreground' : 'hover:text-slate-900'}`}>Terms of Service</Link></li>
            </ul>
          </div>
        </div>

        <div className={`border-t mt-12 pt-8 flex flex-col md:flex-row justify-between items-center ${isDark ? 'border-border' : 'border-slate-200'}`}>
          <p className={`text-sm ${isDark ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            © {new Date().getFullYear()} Eventism. All rights reserved.
          </p>
          <p className={`text-sm mt-2 md:mt-0 ${isDark ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            Denver, Colorado
          </p>
        </div>
      </div>
    </footer>
  );
}
